import {useCallback, useMemo} from "react";
import axios from "axios";
import {message} from "antd";
import {useNavigate} from "react-router-dom";
import {useLotteryToken} from "../LotteryTokenProvider";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ENDPOINT = `${BASE_URL}/happypay/campaigns/happy-pay-cust.php`;

/**
 * Single access point for the lottery endpoints.
 *
 * Every call resolves to `{ok, data}` and never throws: a draw in front of an
 * audience should surface a retryable error, not an unhandled rejection. A 401
 * logs out and redirects once, centrally, so no caller has to remember to.
 */
export const useLotteryApi = () => {
    const {token, logout} = useLotteryToken();
    const navigate = useNavigate();

    const handleUnauthorized = useCallback(() => {
        message.error('Нэвтрэх эрх дууссан байна');
        logout();
        navigate('/happypaylottery/login');
    }, [logout, navigate]);

    const request = useCallback(async (config, errorMessage) => {
        try {
            const response = await axios({
                ...config,
                url: `${ENDPOINT}${config.url}`,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) return {ok: true, data: response.data};

            message.error(errorMessage);
            return {ok: false, data: null};
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
                return {ok: false, data: null};
            }
            console.error(`${config.method} ${config.url} failed:`, error);
            message.error(errorMessage);
            return {ok: false, data: null};
        }
    }, [token, handleUnauthorized]);

    return useMemo(() => ({
        /** @returns {Promise<{ok: boolean, data: {M: Array, F: Array}}>} */
        fetchWinnableLotteries: async () => {
            const {ok, data} = await request(
                {method: 'get', url: '/winnable-lotteries'},
                'Урамшууллын эрхийг татахад алдаа гарлаа'
            );
            // Normalize at the boundary so no consumer can hit `undefined.filter`
            // when the API omits a gender whose pool is exhausted.
            return {ok, data: {M: data?.M || [], F: data?.F || []}};
        },

        /** @returns {Promise<{ok: boolean, data: Array}>} */
        fetchWinners: async () => {
            const {ok, data} = await request(
                {method: 'get', url: '/winners'},
                'Ялагчдын мэдээллийг татахад алдаа гарлаа'
            );
            return {ok, data: Array.isArray(data) ? data : []};
        },

        /** @returns {Promise<{ok: boolean, data: Array}>} */
        fetchAllLotteries: async () => {
            const {ok, data} = await request(
                {method: 'get', url: '/all-lotteries'},
                'Урамшууллын эрхийг татахад алдаа гарлаа'
            );
            return {ok, data: Array.isArray(data) ? data : []};
        },

        /** @returns {Promise<boolean>} true only once the server has persisted the winner. */
        saveWinner: async (winner) => {
            const {ok} = await request(
                {
                    method: 'post',
                    url: '/winners',
                    data: {custno: winner.custno, lottery: winner.lottery, gender: winner.gender},
                },
                'Урамшууллын эрхийг хадгалахад алдаа гарлаа. Дахин оролдоно уу.'
            );
            if (ok) message.success('Амжилттай хадгалагдлаа');
            return ok;
        },

        /** @returns {Promise<boolean>} */
        deleteWinner: async (custno) => {
            const {ok} = await request(
                {method: 'put', url: '/winners', data: {custno}},
                'Ялагчдын мэдээллийг устгахад алдаа гарлаа'
            );
            if (ok) message.success('Ялагчийг амжилттай устгалаа');
            return ok;
        },

        /** @returns {Promise<boolean>} */
        clearWinners: async () => {
            const {ok} = await request(
                {method: 'delete', url: '/winners'},
                'Ялагчдын мэдээллийг устгахад алдаа гарлаа'
            );
            if (ok) message.success('Бүх ялагчдыг амжилттай устгалаа');
            return ok;
        },
    }), [request]);
};
